/** Client-side checks for optional seller preview MP3 before upload. */
export const PREVIEW_MAX_BYTES = 10 * 1024 * 1024;
/** Mirrors the backend's MAX_PREVIEW_SECONDS (beats/preview.js). Checked here so
 *  an over-long clip is caught before upload — server-side it fails the whole
 *  pipeline run and deletes the upload, which is a poor way to find out. */
export const PREVIEW_MAX_UPLOAD_SECONDS = 90;

/** Read audio duration (seconds) via a temporary <audio> element. */
export function probeAudioDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const cleanup = () => {
      URL.revokeObjectURL(url);
      audio.removeAttribute('src');
      audio.load();
    };
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const dur = audio.duration;
      cleanup();
      if (!Number.isFinite(dur) || dur <= 0) {
        reject(new Error('Could not read preview duration — please use a valid MP3'));
        return;
      }
      resolve(dur);
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error('Could not read that preview file — please use a valid MP3'));
    };
    audio.src = url;
  });
}

/**
 * Validate optional preview before upload (format, size, max duration).
 * Shop playback still caps at PREVIEW_PLAYBACK_SECONDS, which is shorter.
 * @returns null if OK, or an error message for the seller.
 */
export async function validatePreviewFile(file: File | null | undefined): Promise<string | null> {
  if (!file) return null;
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (!name.endsWith('.mp3') && type !== 'audio/mpeg' && type !== 'audio/mp3') {
    return 'Preview must be an MP3 file';
  }
  if (file.size > PREVIEW_MAX_BYTES) {
    return 'The optional preview MP3 must be 10 MB or smaller';
  }
  try {
    const duration = await probeAudioDurationSeconds(file);
    if (duration < 1) {
      return 'Preview must be at least 1 second long';
    }
    if (duration > PREVIEW_MAX_UPLOAD_SECONDS) {
      return `Preview must be ${PREVIEW_MAX_UPLOAD_SECONDS} seconds or shorter`;
    }
  } catch (e: any) {
    return e?.message || 'Could not validate preview MP3';
  }
  return null;
}
